'use client';

import { useTrainingAdvisory } from '@/lib/hooks/useSmartWorkout';
import { AlertCircle, CheckCircle, AlertTriangle, XCircle, Activity, Gauge, Calendar } from 'lucide-react';

// Strip emojis and pictographs so the dashboard language stays clean and on-brand
function stripEmojis(text: string): string {
  if (!text) return text;
  // Covers most emoji ranges + misc symbols we don't want in headings
  // Using ES5-compatible approach to avoid 'u' flag issues
  return text
    .replace(/[\uD83C-\uDBFF][\uDC00-\uDFFF]|[\u2600-\u26FF]|[\u2700-\u27BF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Training Advisory Component
 * 
 * Shows at top of dashboard whether training is advisable today.
 * Replaces generic "ready to train" message with ML-driven decision.
 */
export default function TrainingAdvisory() {
  const { loading, summary } = useTrainingAdvisory();

  if (loading) {
    return (
      <div className="bg-gray-800/50 rounded-lg p-4 animate-pulse">
        <div className="h-6 bg-gray-700 rounded w-1/3 mb-2" />
        <div className="h-4 bg-gray-700 rounded w-2/3" />
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const colorClasses = {
    green: 'bg-green-900/30 border-green-700 text-green-400',
    yellow: 'bg-yellow-900/30 border-yellow-700 text-yellow-400',
    orange: 'bg-orange-900/30 border-orange-700 text-orange-400',
    red: 'bg-red-900/30 border-red-700 text-red-400',
  };

  const icons = {
    train: <CheckCircle className="w-6 h-6" />,
    light: <AlertTriangle className="w-6 h-6" />,
    deload: <AlertCircle className="w-6 h-6" />,
    rest: <XCircle className="w-6 h-6" />,
  };

  const bgColor = colorClasses[summary.color];
  const icon = icons[summary.recommendation];

  return (
    <div className={`operator-panel border ${bgColor}`}>
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 mt-1">
          {icon}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-heading font-bold text-base sm:text-lg text-white uppercase tracking-wide">
            {stripEmojis(summary.title)}
          </h3>
          <p className="text-xs sm:text-sm text-gray-300 mt-1">
            {stripEmojis(summary.subtitle)}
          </p>

          {/* Metrics */}
          <div className="flex flex-wrap gap-4 mt-3 text-sm opacity-75">
            <div className="flex items-center gap-1.5">
              <Activity className="w-4 h-4" />
              <span>Recovery: {summary.metrics.recoveryScore}/100</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Gauge className="w-4 h-4" />
              <span>ACWR: {summary.metrics.acwr.toFixed(2)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{summary.metrics.consecutiveDays}d consecutive</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Compact version for smaller spaces
 */
export function TrainingAdvisoryCompact() {
  const { loading, summary } = useTrainingAdvisory();

  if (loading || !summary) {
    return null;
  }

  const colorClasses = {
    green: 'text-green-400',
    yellow: 'text-yellow-400',
    orange: 'text-orange-400',
    red: 'text-red-400',
  };

  const icons = {
    train: <CheckCircle className="w-4 h-4" />,
    light: <AlertTriangle className="w-4 h-4" />,
    deload: <AlertCircle className="w-4 h-4" />,
    rest: <XCircle className="w-4 h-4" />,
  };

  return (
    <div className={`flex items-center gap-2 ${colorClasses[summary.color]}`}>
      {icons[summary.recommendation]}
      <span className="font-heading text-xs uppercase tracking-wide">
        {stripEmojis(summary.title)}
      </span>
    </div>
  );
}

